import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import _ from 'lodash';
import Select from 'react-select';
import { List, Map } from 'immutable';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Col, Row, Form } from 'react-bootstrap';
import Button from '../ui/Button';
import Loader from '../ui/Loader';
import SearchAdvancedRow from './SearchAdvancedRow';
import { retrieveMetatags, retrieveAdvancedSearchCollections } from '../../actions/session';
import { retrieveCategories } from '../../actions/categories';
import {
  retrieveAdvancedSearch,
  saveSearch,
  retrieveSavedSearchList,
  deleteSavedSearch,
} from '../../actions/umoGallery';
import {
  SearchTerm,
  AdvancedSearchTerm,
  AllowedTypeFilters,
  getTypeLabel,
  SearchParams,
} from '../../utils/searchUtils';
import { getCatDataByID, getKeyPath } from '../../utils/immutableUtils';
import CategoryTree from '../CategoryTree';
import { DEFAULT_COLLECTION_ID } from '../../constants/session';
import { SelectStyles } from '../../../style/components/react-select';
import appConfig from '../../appConfig';

const mapStateToProps = (state) => ({
  categories: state.categories,
  advancedSearchCollectionList: state.session.get('advancedSearchCollectionList'),
  isRequestingAdvancedSearchCollections: state.session.get('isRequestingAdvancedSearchCollections'),
});

const mapDispatchToProps = (dispatch) => ({
  dispatch: (actionToDispatch) => dispatch(actionToDispatch),
  retrieveMetatagsDispatch: (collectionID) => {
    dispatch(retrieveMetatags(collectionID));
  },
  retrieveCategoriesDispatch: (categoryID) => {
    dispatch(retrieveCategories(categoryID));
  },
  retrieveAdvancedSearchCollectionsDispatch: () => {
    dispatch(retrieveAdvancedSearchCollections());
  },
});

function SearchAdvanced({
  dispatch,
  metatags,
  isRequestingCollections,
  collectionsError,
  savedSearches,
  userCollection,
  isAdvancedSearchShown,
  onAdvancedSearchToggle,
  isPublicUsr,
  searchParams,
  intl,
  retrieveMetatagsDispatch,
  retrieveCategoriesDispatch,
  categories,
  advancedSearchCollectionList,
  isRequestingAdvancedSearchCollections,
  retrieveAdvancedSearchCollectionsDispatch,
}) {
  const defaultCollectionID = userCollection || DEFAULT_COLLECTION_ID;
  const [selectedCollectionID, setSelectedCollectionID] = useState(defaultCollectionID);
  const [metatagList, setMetatagList] = useState(metatags?.get(defaultCollectionID) || new List());
  const [metatagOptions, setMetatagOptions] = useState([]);
  const [isMetatagsLoading, setIsMetatagsLoading] = useState(false);
  const [metadataList, setMetadataList] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');
  const [searchNameInvalid, setSearchNameInvalid] = useState(false);
  const [mediaType, setMediaType] = useState(null);
  const [searchSaveMessage, setSearchSaveMessage] = useState(null);
  const [searchCurCollection, setSearchCurCollection] = useState(false);
  const [isSaveSearchShown, setIsSaveSearchShown] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSchemas, setSelectedSchemas] = useState(searchParams.get('schemaList') || []);

  const isRestrictingCollections = !appConfig.advanced.disableAdvancedSearchCollectionRestriction;
  const isLoadingCollectionRestriction = isRestrictingCollections && !advancedSearchCollectionList;

  const visibleCategoryList = useMemo(() => {
    const categoryList = categories.get('categoryList');
    if (!isRestrictingCollections || !categoryList) return categoryList;
    if (!advancedSearchCollectionList) return new List();

    const allowedCollectionIDs = new Set(advancedSearchCollectionList.map((item) => item.get('id')).toJS());
    return categoryList.filter((root) => allowedCollectionIDs.has(root.get('i')));
  }, [categories, advancedSearchCollectionList, isRestrictingCollections]);

  const savedSearchesDisplayOptions = useMemo(() => {
    if (!isPublicUsr && savedSearches && savedSearches.length > 0) {
      return savedSearches.map((item) => ({
        value: item.s[0].rw[0],
        remove: item.s[0].q[0],
        label: item.s[0].n[0],
      }));
    }
    return [];
  }, [isPublicUsr, savedSearches]);

  const mediaTypeOptionsDisplay = useMemo(() => ['', ...AllowedTypeFilters].map((type) => ({
    label: intl.formatMessage({ id: getTypeLabel(type) }),
    value: type,
  })), [intl]);

  const selectedSchemasObjects = useMemo(() => {
    return metatagOptions.filter(schema => selectedSchemas.includes(schema.label));
  }, [metatagOptions, selectedSchemas]);

  const getRangeValueFromQuery = (rangeQuery) => {
    switch (rangeQuery[2]) {
      case 'BETWEEN':
        return (`RANGE:${rangeQuery[3]},${rangeQuery[4]}`);
      case '<':
        return (`LESS:${rangeQuery[3]}`);
      case '>':
        return (`GREATER:${rangeQuery[3]}`);
      default:
        return ('');
    }
  };

  // get the categories and collections that are selected
  // return an object with the collection ID as the key and the categories as the value
  // (or null, if the entire collection is selected)
  const getCategoriesByCollection = () => {
    const categoryList = visibleCategoryList;
    const categoriesByCollection = {};

    selectedCategories.forEach((id) => {
      if (categoryList.some(root => root.get('i') === id)) {
        categoriesByCollection[id] = null;
        return;
      }
      
      const keyPath = getKeyPath(categoryList, 'i', id, 'ct');
      if (!keyPath) return;

      const collectionId = categoryList.getIn([keyPath[0], 'i']);
      if (categoriesByCollection[collectionId] === null) return;
      
      (categoriesByCollection[collectionId] ??= []).push(id);
    });

    return categoriesByCollection;
  };

  const addMetadataRow = (searchTerm) => {
    const metaList = Object.assign([], metadataList);
    metaList.push(searchTerm || new SearchTerm('DEFAULT'));
    setMetadataList(metaList);
    setSelectedSearch('');
  };

  const clearMetadataRowList = () => {
    setSelectedSearch('');
    setSearchNameInvalid(false);
    setSearchName('');
    setSearchSaveMessage(null);

    setMetadataList([]);
  };

  const removeMetadataRow = (rowIndex) => {
    const metaList = Object.assign([], metadataList);
    if (metaList.length === 1) {
      clearMetadataRowList();
    } else {
      metaList.splice(_.toInteger(rowIndex), 1);
      setMetadataList(metaList);
    }
  };

  const retrieveSavedSearch = (search) => {
    let metaSearch;
    let metaVal;
    let metaTag;
    let multiVal;
    const metaOptions = {};
    const metaList = [];
    const selectedCategories = [];
    setSelectedCollectionID(null);
    setSearchCurCollection(false);

    _.forEach(_.split(search, '" '), (value) => {
      metaSearch = _.split(value.replace('"', ''), /(META_NOT|META_RANGE|META_RANGE_NOT|META):/);
      // in case "Any metatag" was used
      if (metaSearch.length === 1) {
        metaSearch[0] = metaSearch[0].replace('"', '');
        metaSearch = _.split(metaSearch[0], 'AID:');
        if (metaSearch.length === 2) {
          setSelectedCollectionID(metaSearch[1]);
          setSearchCurCollection(metaSearch[1]);
          selectedCategories.push(metaSearch[1]);
        } else {
          metaSearch = _.split(metaSearch[0], 'IN_CATEGORIES:');
          if (metaSearch.length === 2) {
            selectedCategories.push(...metaSearch[1].split(',').map(id => id.trim()));
          } else {
            metaSearch = _.split(metaSearch[0], 'COLLECTION_CATEGORY:');
            if (metaSearch.length === 2) {
              // mixed list of collection and category IDs
              // need to separate usage of entire collection ids vs specific categories in collections
              const fullIDList = metaSearch[1].split(',').map(id => id.trim());
              const categoryList = visibleCategoryList;

              const collectionIDs = [];
              const categoryIDs = [];

              fullIDList.forEach(id => {
                if (categoryList?.some(root => root.get('i') === id)) {
                  collectionIDs.push(id);
                } else {
                  const item = getCatDataByID(categoryList, id);
                  const parentCollectionID = item?.get('collpk') || item?.get('i');
                  const index = collectionIDs.indexOf(parentCollectionID);
                  if (index > -1) collectionIDs.splice(index, 1);
                  categoryIDs.push(id);
                }
              });

              selectedCategories.push(...collectionIDs, ...categoryIDs);
            } else {
              metaSearch = _.split(metaSearch, 'IMAGE_TYPE:');
              if (metaSearch.length === 1) {
                metaSearch[0] = metaSearch[0].replace('"', '');
                metaSearch = _.split(metaSearch[0], /(WORDS_NOT|WORDS):/);
                metaOptions.operator = metaSearch[1] === 'WORDS_NOT'
                  ? 'NOT'
                  : metaSearch[0].replace(/\s+/g, '');
                metaList.push(new SearchTerm('DEFAULT', metaSearch[2], metaOptions));
              } else {
                metaSearch[1] = metaSearch[1].replace('"', '');
                setMediaType(metaSearch[1]);
              }
            }
          }
        }
      } else {
        metaVal = _.split(metaSearch[2].replace('"', ''), ',');
        metaSearch[0] = metaSearch[1].includes('NOT') ? 'NOT' : metaSearch[0];
        metaTag = _.split(metaVal[0], '.');
        if (metaSearch[1].includes('RANGE')) {
          const [, metaType] = metaVal;
          metaOptions.type = metaType;
          metaVal[1] = getRangeValueFromQuery(metaVal);
        }
        multiVal = _.split(metaVal[1], / (OR|AND) /);
        // for loop in case query has been optimized and parts were removed-
        // meta:xx AND meta:yy comes back from piction as meta:xx AND yy
        for (let i = 0; i < multiVal.length; i += 2) {
          // if first word- get operator of query, else get operator of word
          metaOptions.operator = i === 0 ? metaSearch[0].replace(/\s+/g, '') : multiVal[i - 1];
          metaList.push(
            new AdvancedSearchTerm(
              metaTag[0],
              metaTag[1],
              multiVal[i],
              metaOptions,
            ),
          );
        }
      }
    });

    if (selectedCategories.length > 0) {
      setSelectedCategories(selectedCategories);
    } else {
      setSelectedCategories(visibleCategoryList?.map(cat => cat.get('i')).toJS() || []);
    }

    setMetadataList(metaList);
  };

  useEffect(() => { // load metatags
    if (!metatags || !metatags.get(defaultCollectionID)) {
      setIsMetatagsLoading(true);
      retrieveMetatagsDispatch(defaultCollectionID);
    } else if (metatags.get(defaultCollectionID) instanceof List) {
      setIsMetatagsLoading(false);
    }
  }, [metatags, defaultCollectionID, retrieveMetatagsDispatch]);

  useEffect(() => { // load categories to show alongside collections
    if ((!categories.get('categoryList') || categories.get('categoryList').isEmpty()) && !categories.get('isRetrievingRoot')) {
      retrieveCategoriesDispatch();
    }
  }, [categories, retrieveCategoriesDispatch]);

  useEffect(() => {
    if (isRestrictingCollections && !advancedSearchCollectionList && !isRequestingAdvancedSearchCollections) {
      retrieveAdvancedSearchCollectionsDispatch();
    }
  }, [
    isRestrictingCollections,
    advancedSearchCollectionList,
    isRequestingAdvancedSearchCollections,
    retrieveAdvancedSearchCollectionsDispatch,
  ]);

  useEffect(() => { // load saved searches
    if (!isPublicUsr && (!savedSearches || savedSearches.size === 0)) {
      dispatch(retrieveSavedSearchList());
    }
  }, [isPublicUsr, savedSearches, dispatch]);

  useEffect(() => { // load existing search
    if (searchParams.get('metadataList')) {
      const selectedCategories = [];
      setMetadataList(Object.assign([], searchParams.get('metadataList')));
      setMediaType(searchParams.get('typeFilter') || '');
      if (searchParams.get('collectionID')) {
        selectedCategories.push(...searchParams.get('collectionID'));
      }
      if (searchParams.get('categoryList')) {
        const categoryList = searchParams.get('categoryList');
        if (typeof categoryList === 'object' && !Array.isArray(categoryList)) {
          Object.entries(categoryList).forEach(([collectionId, cats]) => {
            if (cats && cats.length > 0) {
              selectedCategories.push(...cats);
            } else {
              selectedCategories.push(collectionId);
            }
          });
        } else {
          selectedCategories.push(...categoryList);
        }
      }
      setSelectedCategories(selectedCategories);
    }
  }, [searchParams]);

  useEffect(() => {
    if (metatags && metatags.get(selectedCollectionID)) {
      setMetatagList(metatags.get(selectedCollectionID));
    }
  }, [selectedCollectionID, metatags]);

  useEffect(() => {
    if (metatags && selectedCategories.length > 0) {
      const categoryList = visibleCategoryList;
      const selectedCollectionIDs = [
        ...new Set(
          selectedCategories
            .map(categoryId => {
              if (categoryList?.some(root => root.get('i') === categoryId)) return categoryId;
              const keyPath = getKeyPath(categoryList, 'i', categoryId, 'ct');
              return keyPath ? categoryList.getIn([keyPath[0], 'i']) : null;
            })
            .filter(Boolean),
        ),
      ];
      let filteredMetatags = new List();
      const seen = new Set(); // use of set to check for duplicate metatags faster

      selectedCollectionIDs.forEach((collectionId) => {
        let collectionMetatags = metatags.get(collectionId);
        // for CC level categories use default collection metatags
        if (!collectionMetatags) {
          collectionMetatags = metatags.get(defaultCollectionID);
        }
        collectionMetatags.forEach((metatag) => {
          const key = `${metatag.getIn(['CATEGORY', 0])}.${metatag.getIn(['TAG_NAME', 0])}`;
          if (!seen.has(key)) {
            seen.add(key);
            filteredMetatags = filteredMetatags.push(metatag);
          }
        });
      });
      setMetatagList(filteredMetatags);
    } else {
      setMetatagList(metatags.get(defaultCollectionID));
    }
  }, [selectedCategories, metatags, visibleCategoryList, defaultCollectionID]);

  useEffect(() => {
    if (metatagList && metatagList instanceof List) {
      const newMetatagOptions = [];

      metatagList.forEach((metatag) => {
        const category = metatag.getIn(['CATEGORY', 0]);

        let categoryGroup = newMetatagOptions.find(group => group.id === category);

        if (!categoryGroup) {
          categoryGroup = {
            id: category,
            label: category,
            options: [],
          };
          newMetatagOptions.push(categoryGroup);
        }

        if (metatag.getIn(['TYPE', 0]) !== 'RGROUP') {
          categoryGroup.options.push({
            value: `${metatag.getIn(['CATEGORY', 0])}.${metatag.getIn(['TAG_NAME', 0])}`,
            label: metatag.getIn(['DISPLAY_VALUE', 0]) || metatag.getIn(['TAG_NAME', 0]),
            type: metatag.getIn(['TYPE', 0]),
          });
        }
      });

      setMetatagOptions(newMetatagOptions);

      if (selectedSchemas.length === 0) {
        setSelectedSchemas(newMetatagOptions.map(s => s.label));
      } else {
        const wasSelectAll = selectedSchemas.length === metatagOptions.length && 
          selectedSchemas.every(val => metatagOptions.some(option => option.label === val));
        
        if (wasSelectAll) {
          setSelectedSchemas(newMetatagOptions.map(s => s.label));
        } else {
          setSelectedSchemas(selectedSchemas.filter(val => newMetatagOptions.some(option => option.label === val)));
        }
      }
    }
  }, [metatagList]);

  const onAddClick = (e) => {
    e.preventDefault();
    addMetadataRow();
  };

  const onRowChange = (rowIndex, searchTerm) => {
    const metaList = Object.assign([], metadataList);
    metaList[rowIndex] = searchTerm;
    setMetadataList(metaList);
    setSelectedSearch('');
  };

  const onRemoveClick = (e, rowIndex) => {
    e.preventDefault();
    removeMetadataRow(rowIndex);
  };

  const onSubmit = (e) => {
    if (e) e.preventDefault();
    const categoriesByCollection = getCategoriesByCollection();
    dispatch(
      retrieveAdvancedSearch(
        metadataList,
        mediaType,
        searchCurCollection ? selectedCollectionID : null,
        categoriesByCollection,
        null, // queryName
        selectedSchemas,
      ),
    );
    onAdvancedSearchToggle();
  };

  const onSearchChange = (search) => {
    setSelectedSearch(search?.value ? search : '');
    setMediaType(null);
    if (search) {
      retrieveSavedSearch(search.value);
    }
  };

  const clearAll = () => {
    clearMetadataRowList();
    setSelectedCollectionID(defaultCollectionID);
    setSelectedCategories([]);
    setSelectedSchemas([]);
    setMediaType(null);
  };

  const saveCurrentSearch = () => {
    if (_.isEmpty(searchName)) {
      setSearchNameInvalid(true);
      setSearchSaveMessage(intl.formatMessage({ id: 'searchBar.advanced.searchNameMissing' }));
    } else {
      const categoriesByCollection = getCategoriesByCollection();
      dispatch(
        saveSearch(
          metadataList,
          mediaType,
          searchCurCollection ? selectedCollectionID : null,
          categoriesByCollection,
          searchName,
          selectedSchemas,
        ),
      )

        .then((res) => {
          if (res === 'save exists') {
            setSearchNameInvalid(true);
            setSearchSaveMessage(intl.formatMessage({ id: 'searchBar.advanced.saveSearchExists' }));
          } else {
            setSearchNameInvalid(false);
            setSearchName('');
            setSearchSaveMessage(null);
            setIsSaveSearchShown(false);
            onSubmit();
          }
          return res;
        })
        .catch((err) => {
          setSearchSaveMessage(intl.formatMessage({ id: 'searchBar.advanced.searchSaveError' }));
          console.log(err);
          return err;
        });
    }
  };

  const deleteSearch = () => {
    dispatch(deleteSavedSearch(selectedSearch.remove))
      .then(() => dispatch(retrieveSavedSearchList()));
    setSelectedSearch('');
  };

  const onCategorySelect = (selectedKeys) => {
    if (selectedCategories.length === 0 && selectedKeys.length > 0 && metadataList.length === 0) {
      addMetadataRow();
    } else if (selectedCategories.length > 0 && selectedKeys.length === 0) {
      clearMetadataRowList();
    }
    setSelectedCategories(selectedKeys);
  };

  return (
    <div className={classNames('search-advanced', { 'search-advanced--open': isAdvancedSearchShown })}>
      {isMetatagsLoading ? (
        <Loader className="search-advanced__loader" isLarge />
      ) : (
        <Form className="search-advanced__form" onSubmit={onSubmit}>
          <Col md={{ span: 3 }}>
            <div className="form-text">
              {intl.formatMessage({ id: 'searchBar.advanced.selectCollectionLabel' })}
            </div>
            <Row className="search-advanced__category-tree">
              <CategoryTree
                categoryList={visibleCategoryList}
                onSelect={onCategorySelect}
                isRetrievingRoot={categories.get('isRetrievingRoot') || isLoadingCollectionRestriction}
                selectedCategories={selectedCategories}
                showCheckboxes
                disableExpand
              />
            </Row>
            <Row className="search-advanced__row">
              <Form.Label className="search-advanced__error">
                {isRequestingCollections ? (<Loader />) : collectionsError}
              </Form.Label>
            </Row>
          </Col>
          <Col md={{ span: 2 }}>
            {selectedCategories.length > 0 && metatagOptions.length > 0 ?
              <div className="search-advanced__row">
                <div className="form-text search-advanced__query-title">
                  {intl.formatMessage({ id: 'searchBar.advanced.availableSchemas' })}
                </div>
                <Form.Group className="search-advanced__schema-filter-checkbox">
                  <Form.Check
                    type="checkbox"
                    id="schema_select-all"
                    key="schema_select-all"
                    label={intl.formatMessage({ id: 'searchBar.advanced.selectAllSchemas' })}
                    checked={selectedSchemas.length === metatagOptions.length && selectedSchemas.every(val => metatagOptions.some(option => option.label === val))}
                    onChange={(e) => setSelectedSchemas(e.target.checked ? metatagOptions.map(s => s.label) : [])}
                  />
                  {
                    _.map(metatagOptions, (option) => (
                      <Form.Check
                        type="checkbox"
                        id={`schema-${option.label}`}
                        key={`schema-${option.label}`}
                        label={option.label}
                        checked={selectedSchemas.includes(option.label)}
                        onChange={(e) => setSelectedSchemas(e.target.checked ? [...selectedSchemas, option.label] : selectedSchemas.filter(val => val !== option.label))}
                      />
                    ))
                  }
                </Form.Group>
              </div> : null}
          </Col>
          <Col md={{ span: 7 }}>
            {!isPublicUsr ? (
              <Row className="search-advanced__row">
                <label className="form-text" htmlFor="search-advanced__saved-search-select">
                  {intl.formatMessage({ id: 'searchBar.advanced.savedSearches' })}
                </label>
                <Form.Group className="search-advanced__saved-search">
                  <Select
                    value={selectedSearch}
                    className="search-advanced__saved-search-select col-sm-6"
                    placeholder={intl.formatMessage({ id: 'searchBar.advanced.savedSearches' })}
                    onChange={onSearchChange}
                    options={savedSearchesDisplayOptions}
                    openOnFocus
                    size="sm"
                    styles={SelectStyles}
                  />
                  {selectedSearch !== '' ? (
                    <Button
                      className="search-advanced__saved-search-button"
                      onClick={deleteSearch}
                      variant="danger"
                      size="md"
                    >
                      <span>
                        <i className="search-advanced__del-search-icon" />
                        {_.truncate(
                          intl.formatMessage({ id: 'searchBar.advanced.deleteSearchButton' }, { searchName: selectedSearch.label }),
                          { length: 15 },
                        )}
                      </span>
                    </Button>
                  ) : null}
                </Form.Group>
              </Row>
            ) : null}
            {selectedCategories.length > 0 || metadataList.length > 0 ? (
              <div className="search-advanced__query-builder">
                <div className="form-text search-advanced__query-title">
                  {intl.formatMessage({ id: 'searchBar.advanced.searchQuery' })}
                </div>
                <Row className="search-advanced__row">
                  <label className="form-text" htmlFor="advanced-input-dropdown-addon">
                    {intl.formatMessage({ id: 'searchBar.advanced.mediaType' })}
                  </label>
                  <Select
                    className="search-advanced__type-input col-sm-4"
                    onChange={e => setMediaType(e.value)}
                    id="advanced-input-dropdown-addon"
                    value={{
                      label: intl.formatMessage({ id: getTypeLabel(mediaType) }),
                      value: mediaType,
                    }}
                    size="sm"
                    options={mediaTypeOptionsDisplay}
                    styles={SelectStyles}
                  />
                </Row>
                <div className="form-text search-advanced__query-title search-advanced__metadata-title">
                  {intl.formatMessage({ id: 'searchBar.advanced.selectMetadataLabel' })}
                </div>
                {isMetatagsLoading || metadataList.length === 0 ? null
                  : _.map(metadataList, (searchTerm, index) => (
                    <SearchAdvancedRow
                      rowIndex={index}
                      onDataChange={onRowChange}
                      onRemoveClick={onRemoveClick}
                      searchTerm={searchTerm}
                      metatagOptions={selectedSchemasObjects}
                      key={index}
                    />
                  ))}
                <Row className="search-advanced__row">
                  <Form.Group>
                    <button
                      type="button"
                      onClick={onAddClick}
                      className="search-advanced__metatag-add btn-sm"
                    >
                      <i className="search-advanced__metatag-icon" />
                      {intl.formatMessage({ id: 'searchBar.advanced.addMetatagButton' })}
                    </button>
                  </Form.Group>
                </Row>
                <Row className="search-advanced__row">
                  <Form.Group className="search-advanced__search-button-group">
                    <Button
                      size="sm"
                      variant="primary"
                      className="search-advanced__search-button"
                      onClick={() => setIsSaveSearchShown(true)}
                    >
                      {intl.formatMessage({ id: 'searchBar.advanced.saveSearchButton' })}
                    </Button>
                    <Button
                      size="sm"
                      type="submit"
                      variant="primary"
                      className="search-advanced__search-button"
                    >
                      {intl.formatMessage({ id: 'searchBar.advanced.searchButton' })}
                    </Button>
                    <Button
                      size="md"
                      variant="default"
                      className="search-advanced__search-button btn-outline-primary"
                      onClick={clearAll}
                    >
                      {intl.formatMessage({ id: 'searchBar.advanced.clearButton' })}
                    </Button>
                  </Form.Group>
                </Row>
              </div>
            ) : (
              <div className="form-text search-advanced__no-collection-selected">
                {intl.formatMessage({ id: 'searchBar.advanced.selectToStart' })}
              </div>
            )}
            {!isPublicUsr && isSaveSearchShown && (
              <div
                className="search-advanced__save-popup-overlay"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setIsSaveSearchShown(false);
                    setSearchNameInvalid(false);
                    setSearchSaveMessage(null);
                    setSearchName('');
                  }
                }}
              >
                <div className="search-advanced__save-popup">
                  <div className="search-advanced__save-popup-header">
                    <span className="search-advanced__save-popup-title">
                      {intl.formatMessage({ id: 'searchBar.advanced.saveSearchButton' })}
                    </span>
                    <button
                      type="button"
                      className="search-advanced__save-popup-close"
                      onClick={() => {
                        setIsSaveSearchShown(false);
                        setSearchNameInvalid(false);
                        setSearchSaveMessage(null);
                        setSearchName('');
                      }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  <div className="search-advanced__save-popup-body">
                    <Form.Group
                      className="search-advanced__save-search"
                      validated={searchNameInvalid ? 'invalid' : ''}
                    >
                      <Form.Control
                        placeholder={intl.formatMessage({ id: 'searchBar.advanced.newSearchName' })}
                        onChange={(e) => setSearchName(e.target.value)}
                        value={searchName}
                        type="text"
                        className="search-advanced__save-popup-input"
                        size="sm"
                        autoFocus
                      />
                      {searchSaveMessage && (
                        <div className="search-advanced__save-popup-error">
                          {searchSaveMessage}
                        </div>
                      )}
                    </Form.Group>
                    <div className="search-advanced__save-popup-actions">
                      <Button
                        className="search-advanced__save-popup-cancel"
                        onClick={() => {
                          setIsSaveSearchShown(false);
                          setSearchNameInvalid(false);
                          setSearchSaveMessage(null);
                          setSearchName('');
                        }}
                        variant="outline-primary"
                        size="sm"
                      >
                        {intl.formatMessage({ id: 'general.cancel' })}
                      </Button>
                      <Button
                        className="search-advanced__save-popup-save"
                        onClick={saveCurrentSearch}
                        variant="primary"
                        size="sm"
                      >
                        {intl.formatMessage({ id: 'searchBar.advanced.saveSearchButton' })}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Form>
      )}
    </div>
  );
}

SearchAdvanced.propTypes = {
  dispatch: PropTypes.func,
  metatags: PropTypes.instanceOf(Map),
  isRequestingCollections: PropTypes.bool,
  collectionsError: PropTypes.instanceOf(Error),
  savedSearches: PropTypes.oneOfType([
    PropTypes.instanceOf(Array),
    PropTypes.instanceOf(List),
  ]),
  userCollection: PropTypes.string,
  isAdvancedSearchShown: PropTypes.bool,
  onAdvancedSearchToggle: PropTypes.func,
  isPublicUsr: PropTypes.bool,
  searchParams: PropTypes.instanceOf(SearchParams),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  retrieveMetatagsDispatch: PropTypes.func,
  retrieveCategoriesDispatch: PropTypes.func,
  categories: PropTypes.instanceOf(Map),
  advancedSearchCollectionList: PropTypes.instanceOf(List),
  isRequestingAdvancedSearchCollections: PropTypes.bool,
  retrieveAdvancedSearchCollectionsDispatch: PropTypes.func,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(SearchAdvanced));
